import {
  QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { fetchCreateKycFaceRecognition } from './service';
import { KYCQueryKeys } from '../../queryKeys';
import { UseCreateKycFaceRecognitionProps } from './props';
import { useAxiosInstance } from '@starsoft/common/hooks';
import { AxiosInstance } from 'axios';
import { GenerateFaceRecognitionLinkOutputDto } from '@starsoft/common/dto/kyc/generate-face-recognition-link-output.dto';
import { GenerateFaceRecognitionLinkInputDto } from '@starsoft/common/dto/kyc/generate-face-recognition-link-input.dto';

/**
 * Custom hook to create and manage KYC face recognition verification
 *
 * This hook handles generating face recognition verification links and managing the mutation state.
 * It uses React Query for data mutation and cache invalidation.
 * When successful, it automatically opens the verification URL in a new tab.
 *
 * @param {UseCreateKycFaceRecognitionProps} options - Optional configuration object
 * @param {Function} [options.externalOnSuccess] - Optional callback function to be called after successful link generation
 *
 * @returns {Object} An object containing:
 *   - createKycFaceRecognition: Function to trigger face recognition link generation (non-async)
 *   - createKycFaceRecognitionAsync: Function to trigger face recognition link generation (async version)
 *   - isPending: Boolean indicating if mutation is in progress
 */
export function useCreateKycFaceRecognition(
  options?: UseCreateKycFaceRecognitionProps,
) {
  const axiosInstance: AxiosInstance = useAxiosInstance();

  /**
   * Success callback handler for face recognition link generation
   * Invalidates relevant queries, opens verification URL, and calls external success handler if provided
   *
   * @param {GenerateFaceRecognitionLinkOutputDto} data - The generated link data returned from the server
   */

  const { mutate, isPending, mutateAsync } = useMutation({
    mutationFn: (data: GenerateFaceRecognitionLinkInputDto) =>
      fetchCreateKycFaceRecognition(data, axiosInstance),
  });

  return {
    createKycFaceRecognition: mutate,
    createKycFaceRecognitionAsync: mutateAsync,
    isPending,
  };
}
